/**
 * VendorGoogleReviews — Iter 67ab.
 *
 * Renders the live (or 6h-cached) Google Business Profile reviews for a vendor
 * alongside our existing in-platform `CarReviewsList`. The component is fully
 * self-contained: when the operator hasn't configured the Google Places API
 * key, OR when the vendor hasn't supplied a Place ID, OR when the upstream
 * fetch fails, it renders nothing — no "feature disabled" placeholder cluttering
 * the car page.
 *
 * Design intent: makes the vendor look more legitimate by showing third-party
 * social proof, without replacing the rich on-platform review system. Users see
 * the Google logo, star count, and a tight "newest first" review carousel.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Star, ExternalLink } from "lucide-react";

const GOOGLE_G_LOGO = "data:image/svg+xml;utf8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'>
    <path fill='#FFC107' d='M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z'/>
    <path fill='#FF3D00' d='M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z'/>
    <path fill='#4CAF50' d='M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z'/>
    <path fill='#1976D2' d='M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z'/>
  </svg>`
);

export default function VendorGoogleReviews({ vendorId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vendorId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/vendors/${vendorId}/google-reviews`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vendorId]);

  // Hide entirely when the integration isn't enabled, or the vendor hasn't
  // wired their Place ID, or the upstream fetch errored. The on-platform
  // review block already covers the "no reviews" scenario gracefully.
  if (loading || !data || !data.enabled || !data.place_id) return null;
  if (!data.reviews || data.reviews.length === 0) return null;

  const rating = Number(data.rating || 0);
  const total = Number(data.total || 0);
  const mapsUrl = `https://search.google.com/local/reviews?placeid=${encodeURIComponent(data.place_id)}`;

  return (
    <div className="py-8 border-t border-border" data-testid="vendor-google-reviews">
      <div className="flex items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <img src={GOOGLE_G_LOGO} alt="Google" className="h-7 w-7 shrink-0" />
          <div>
            <div className="font-display text-lg font-medium" data-testid="google-reviews-title">
              Google reviews
            </div>
            <div className="flex items-center gap-2 text-xs text-neutral-600 mt-0.5" data-testid="google-reviews-summary">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-3.5 w-3.5 ${i < Math.round(rating) ? "fill-amber-500 text-amber-500" : "text-neutral-300"}`}
                  />
                ))}
              </div>
              <span className="font-semibold text-neutral-900">{rating.toFixed(1)}</span>
              <span>· {total.toLocaleString()} review{total === 1 ? "" : "s"} on Google</span>
            </div>
          </div>
        </div>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-900 hover:underline"
          data-testid="google-reviews-external-link"
        >
          See all on Google <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="google-reviews-list">
        {data.reviews.slice(0, 4).map((r, i) => (
          <GoogleReviewCard key={`${r.author || "anon"}-${r.relative_time || ""}-${i}`} review={r} index={i} />
        ))}
      </div>
    </div>
  );
}

function GoogleReviewCard({ review, index }) {
  const [imgFailed, setImgFailed] = useState(false);
  const initial = (review.author || "?").trim()[0]?.toUpperCase() || "?";

  return (
    <div className="rounded-2xl border border-border bg-white p-4 hover:border-neutral-300 transition" data-testid={`google-review-card-${index}`}>
      <div className="flex items-center gap-3 mb-2.5">
        {!imgFailed && review.author_photo ? (
          <img
            src={review.author_photo}
            alt={review.author}
            className="h-9 w-9 rounded-full object-cover shrink-0"
            onError={() => setImgFailed(true)}
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-sky-100 to-indigo-100 flex items-center justify-center text-xs font-semibold text-indigo-700 shrink-0">
            {initial}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{review.author}</div>
          <div className="flex items-center gap-1.5 text-[11px] text-neutral-500 mt-0.5">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-3 w-3 ${i < Math.round(review.rating || 0) ? "fill-amber-500 text-amber-500" : "text-neutral-300"}`}
                />
              ))}
            </div>
            {review.relative_time && <span>· {review.relative_time}</span>}
          </div>
        </div>
      </div>
      {review.text && (
        <p className="text-sm text-neutral-700 line-clamp-5 leading-relaxed">{review.text}</p>
      )}
    </div>
  );
}
